<script setup lang="ts">
import { StudioEvent } from '@/modules/Studios/types'
import { computed, ref } from 'vue'
import { useImage } from '@/shared/composables/image'
import { getDateFormatted } from '@/services/date'
import { mdiChevronDown, mdiChevronUp } from '@mdi/js'

const props = withDefaults(
  defineProps<{
    event: StudioEvent
    isExpandable?: boolean
  }>(),
  {
    isExpandable: true,
  }
)

const { getEventImg } = useImage()

const isExpanded = ref(!props.isExpandable)
const eventDate = computed(() => getDateFormatted(new Date(props.event.dateStart)))

const getRankText = (rank: number): string => {
  if (rank === 1) return '1st'
  if (rank === 2) return '2nd'
  if (rank === 3) return '3rd'
  if (rank === 4) return 'Top 4'
  if (rank === 5) return 'Top 5'
  return ''
}

const getRankIconClass = (rank: number): string => {
  if (rank === 1) return 'rank-icon icon-gold'
  if (rank === 2) return 'rank-icon icon-silver'
  if (rank === 3) return 'rank-icon icon-bronze'
  return 'rank-icon icon-compete'
}
</script>

<template>
  <div
    class="studio-event-chip"
    :class="{ 'studio-event-chip--expanded': isExpanded }"
  >
    <button
      class="event-header"
      @click="isExpanded = !isExpanded"
    >
      <div class="event-header__left">
        <v-img
          :src="getEventImg(event.image || '', 64)"
          :alt="event.title"
          class="event-header__image"
        />
        <span class="event-header__title">
          {{ event.title }}
        </span>
      </div>
      <div class="event-header__right">
        <span
          v-if="event.city"
          class="event-header__location"
        >
          {{ event.city }}{{ event.state ? `, ${event.state}` : '' }}
        </span>
        <span class="event-header__date">
          {{ eventDate }}
        </span>
        <v-icon
          v-if="isExpandable"
          class="event-header__icon"
        >
          {{ isExpanded ? mdiChevronUp : mdiChevronDown }}
        </v-icon>
      </div>
    </button>
    <v-expand-transition>
      <div
        v-if="isExpanded"
        class="event-details"
      >
        <div class="event-details__battles">
          <div
            v-for="battle in event.battles"
            :key="battle.id"
            class="battle-item"
          >
            <span class="battle-item__title"> {{ battle.title }} {{ battle.crewSize }} vs {{ battle.crewSize }} </span>
            <span class="battle-item__rank">
              {{ getRankText(battle.rank) }} <span :class="getRankIconClass(battle.rank)" />
            </span>
          </div>
        </div>
        <div class="event-details__actions">
          <BKButton
            class="event-details__go-button"
            white-text
            :to="{ name: 'Event', params: { id: String(props.event.id) } }"
          >
            To Event
          </BKButton>
        </div>
      </div>
    </v-expand-transition>
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/style/mixins';

.studio-event-chip {
  display: flex;
  flex-direction: column;
  padding: 20px 20px 4px;
  background-color: var(--color-grey-700);
  border: 1px solid var(--color-grey-700);
  border-radius: var(--border-radius-rounded);
  transition:
    background-color 0.3s,
    border-color 0.3s;

  &--expanded {
    background-color: var(--color-white);
    border-color: var(--color-grey-600);
  }

  .event-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;

    &__left {
      display: flex;
      gap: 8px;
      align-items: center;
    }

    &__image {
      width: 32px;
      height: 32px;
      border-radius: 4px;
    }

    &__right {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      place-items: center;
      text-align: left;
    }

    &__icon {
      margin-left: auto;
    }
  }

  .event-details {
    &__battles {
      display: flex;
      flex-direction: column;
      gap: 16px;
      padding-bottom: 16px;
    }

    &__actions {
      display: flex;
      justify-content: flex-end;
      padding-bottom: 16px;
    }

    &__go-button {
      width: fit-content !important;
      min-width: 120px;
      height: 36px !important;
    }
  }

  .battle-item {
    display: flex;
    gap: 16px;
    justify-content: space-between;

    &__title,
    &__rank {
      @include font-body2;

      color: var(--color-grey-300);
    }

    &__rank {
      display: flex;
      gap: 4px;
      align-items: center;
    }
  }

  .rank-icon {
    display: inline-block;
    width: 24px;
    height: 24px;
    background-size: contain;

    &.icon-gold {
      background-image: url('/img/ic_Trophy-gold.svg');
    }

    &.icon-silver {
      background-image: url('/img/ic_Trophy-silver.svg');
    }

    &.icon-bronze {
      background-image: url('/img/ic_Trophy-bronze.svg');
    }

    &.icon-compete {
      background-image: url('/img/ic_Compete-grey.svg');
    }
  }
}
</style>
